import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CreditCard, Users, ChartLineUp, TrendUp, Crown, Star,
  Storefront, Buildings, Lightning, Package, ToggleRight, Sparkle, Lock, Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { clearMonetizationModeCache } from '../../hooks/useEntitlement';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon size={14} weight="duotone" style={{ color: accent }} />
        </div>
        <span className="text-[11px] text-[#555] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-[#444] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminMonetization() {
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null);
  const [usageOverview, setUsageOverview] = useState([]);
  const [simulation, setSimulation] = useState(null);
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, pRes, mRes, uRes, simRes] = await Promise.all([
          axios.get(`${API}/api/admin-console/monetization-stats`, { withCredentials: true }),
          axios.get(`${API}/api/admin-console/plan-distribution`, { withCredentials: true }),
          axios.get(`${API}/api/monetization/mode`),
          axios.get(`${API}/api/monetization/admin/usage-overview`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/monetization/admin/simulate-enforcement`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setStats(sRes.data);
        setPlans(pRes.data);
        setMode(mRes.data);
        setUsageOverview(uRes.data || []);
        setSimulation(simRes.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const updateMode = async (newMode) => {
    setSavingMode(true);
    try {
      await axios.put(`${API}/api/monetization/admin/mode`, { mode: newMode }, { withCredentials: true });
      const mRes = await axios.get(`${API}/api/monetization/mode`);
      setMode(mRes.data);
      clearMonetizationModeCache();
      toast.success(`Monetization mode set to "${newMode}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update mode');
    }
    setSavingMode(false);
  };

  if (loading) return (
    <div className="space-y-4" data-testid="monetization-loading">
      <div className="h-7 w-56 bg-white/5 rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-white/[0.03] rounded-lg animate-pulse" />)}
      </div>
    </div>
  );

  const PLAN_COLORS = {
    player_free: '#6B7280', player_plus: '#3B82F6', player_pro: '#9D4CDD',
    shop_free: '#6B7280', shop_starter: '#10B981', shop_pro: '#F59E0B',
    publisher_free: '#6B7280',
  };

  return (
    <div data-testid="admin-monetization-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Monetization</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Plan distribution, conversions, revenue metrics, and global mode control</p>
      </div>

      {/* Monetization Mode Control */}
      {mode && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4" data-testid="monetization-mode-card">
          <h2 className="text-[14px] font-bold text-white mb-1 flex items-center gap-2">
            <ToggleRight size={16} weight="duotone" className="text-[#00F0FF]" /> Monetization Mode
          </h2>
          <p className="text-[12px] text-[#555] mb-4">Switch between launch (free), preview (visible plans), and enforced (paid) modes.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            {[
              { code: 'disabled', label: 'Disabled', desc: 'All features free. Paywalls hidden.', icon: Sparkle, color: '#10B981' },
              { code: 'soft', label: 'Soft', desc: 'Plans visible. No enforcement.', icon: Eye, color: '#F59E0B' },
              { code: 'enabled', label: 'Enabled', desc: 'Full enforcement of plan limits.', icon: Lock, color: '#9D4CDD' },
            ].map(m => {
              const active = mode.mode === m.code;
              return (
                <button key={m.code} onClick={() => !active && updateMode(m.code)} disabled={savingMode || active}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    active ? 'border-white/15 bg-white/[0.04]' : 'border-white/[0.06] hover:border-white/[0.12]'
                  }`}
                  style={active ? { borderColor: `${m.color}50`, backgroundColor: `${m.color}10` } : {}}
                  data-testid={`mode-option-${m.code}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <m.icon size={16} weight={active ? 'fill' : 'duotone'} style={{ color: m.color }} />
                    {active && <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: m.color }}>Active</span>}
                  </div>
                  <p className="text-white font-semibold text-[13px]">{m.label}</p>
                  <p className="text-[11px] text-[#555] mt-0.5">{m.desc}</p>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#444]">
            Tracking is <span className="text-[#10B981] font-medium">always active</span> regardless of mode — usage data informs future limit decisions.
          </p>
        </div>
      )}

      {/* Simulation: what would change if we switched to enabled? */}
      {simulation && simulation.simulated_breaches?.some(b => b.would_be_blocked > 0) && (
        <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/25 rounded-xl p-4 mb-4" data-testid="simulation-card">
          <h3 className="text-[13px] font-bold text-[#F59E0B] mb-2">If you flipped to "enabled" right now:</h3>
          <ul className="space-y-1">
            {simulation.simulated_breaches.filter(b => b.would_be_blocked > 0).slice(0, 5).map((b, i) => (
              <li key={i} className="text-[12px] text-white flex items-center justify-between">
                <span><span className="text-[#F59E0B]">{b.would_be_blocked}</span> {b.plan_code} users would breach <span className="text-[#888]">{b.feature_code}</span> (limit {b.limit})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="Total Users" value={stats.total_users} accent="#3B82F6" />
          <StatCard icon={Crown} label="Paid Players" value={stats.paid_players} accent="#9D4CDD" sub={`${stats.player_conversion_rate}% conversion`} />
          <StatCard icon={Storefront} label="Total Shops" value={stats.total_shops} accent="#10B981" />
          <StatCard icon={Star} label="Paid Shops" value={stats.paid_shops} accent="#F59E0B" sub={`${stats.shop_conversion_rate}% conversion`} />
          <StatCard icon={Buildings} label="Publishers" value={stats.total_publishers} accent="#EC4899" />
          <StatCard icon={Lightning} label="Active Entitlements" value={stats.total_entitlements} accent="#00F0FF" />
          <StatCard icon={ChartLineUp} label="Saved Alerts" value={stats.total_saved_alerts} accent="#F59E0B" sub="Across all users" />
          <StatCard icon={Package} label="Campaigns Sent" value={stats.total_campaign_deliveries} accent="#9D4CDD" />
        </div>
      )}

      {/* Plan Distribution */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
        <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
          <CreditCard size={16} weight="duotone" className="text-[#F59E0B]" /> Plan Distribution
        </h2>
        <div className="space-y-3">
          {plans.map(p => {
            const pct = stats?.total_users ? Math.round((p.count / stats.total_users) * 100) : 0;
            const color = PLAN_COLORS[p.plan_code] || '#888';
            return (
              <div key={p.plan_code} data-testid={`plan-${p.plan_code}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-semibold text-white">{p.plan_code.replace(/_/g, ' ')}</span>
                    <span className="text-[10px] text-[#555]">{p.entity_type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-bold text-white">{p.count}</span>
                    <span className="text-[10px] text-[#555]">{pct}%</span>
                  </div>
                </div>
                <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Feature Usage */}
      {stats && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
          <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
            <TrendUp size={16} weight="duotone" className="text-[#10B981]" /> Feature Engagement
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: 'Sessions Created', value: stats.sessions_count, color: '#9D4CDD' },
              { label: 'Tournaments', value: stats.tournaments_count, color: '#F59E0B' },
              { label: 'Leagues', value: stats.leagues_count, color: '#EF4444' },
              { label: 'Recurring Groups', value: stats.groups_count, color: '#3B82F6' },
              { label: 'Bot Practice', value: stats.bot_sessions_count, color: '#00F0FF' },
              { label: 'Favorite Shops', value: stats.favorite_shops_count, color: '#10B981' },
            ].map(f => (
              <div key={f.label} className="px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <p className="text-lg font-bold text-white">{f.value}</p>
                <p className="text-[10px] text-[#555]">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live Feature Usage (lifetime, all modes) */}
      {usageOverview.length > 0 && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mt-4" data-testid="usage-overview">
          <h2 className="text-[14px] font-bold text-white mb-1 flex items-center gap-2">
            <Lightning size={16} weight="duotone" className="text-[#00F0FF]" /> Live Feature Usage (Lifetime)
          </h2>
          <p className="text-[11px] text-[#555] mb-4">Tracked across all monetization modes — informs future limit decisions.</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {usageOverview.slice(0, 25).map((u, i) => (
              <div key={`${u.feature_code}-${u.tenant_type}-${i}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.02]">
                <div className="min-w-0">
                  <p className="text-white text-[12px] font-medium truncate">{u.feature_name}</p>
                  <p className="text-[10px] text-[#555]">{u.tenant_type} · {u.unique_entities} unique</p>
                </div>
                <span className="text-[#00F0FF] text-sm font-bold tabular-nums">{u.total_usage.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
